import { type FC, type InputHTMLAttributes, useState } from 'react';

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
}

/** Password input with a visibility toggle (👁 / 👁‍🗨). */
export const PasswordInput: FC<PasswordInputProps> = ({
  label,
  error,
  helperText,
  id,
  style,
  ...props
}) => {
  const [visible, setVisible] = useState(false);
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
      {label && (
        <label
          htmlFor={inputId}
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500,
            color: 'var(--color-text)',
          }}
        >
          {label}
        </label>
      )}
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          id={inputId}
          type={visible ? 'text' : 'password'}
          style={{
            padding: '8px 40px 8px 12px',
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-text)',
            border: `1px solid ${error ? 'var(--color-danger)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-md)',
            outline: 'none',
            transition: 'var(--transition-fast)',
            width: '100%',
            ...style,
          }}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          aria-pressed={visible}
          title={visible ? 'Masquer' : 'Afficher'}
          style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            height: 30,
            width: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            fontSize: 16,
            padding: 0,
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {visible ? '🙈' : '👁'}
        </button>
      </div>
      {error && (
        <span
          id={`${inputId}-error`}
          role="alert"
          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
        >
          {error}
        </span>
      )}
      {helperText && !error && (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {helperText}
        </span>
      )}
    </div>
  );
};

export default PasswordInput;
